import cookieHandler from '../handlers/cookieHandler'
import type { AuthConfig, IsAuthenticatedOptions, Provider } from '../types/types'
import { authApiHandler } from '../services/authApi'
import tokenHandler from '../handlers/tokenHandler'
import { AccessTokenData, IdTokenData } from '../types/tokenTypes'

export default (config: AuthConfig, cookieHeader?: string) => {
  const cookie = cookieHandler(cookieHeader)
  const token = tokenHandler(config)
  const authApi = authApiHandler(config)

  const setCookies = (
    provider: Provider,
    idToken: string,
    refreshToken: string,
    accessToken: string
  ) => {
    const isPunchout = provider === 'dustin_punchout'

    cookie.setProvider(provider)
    cookie.setIdToken(idToken, isPunchout)
    cookie.setRefreshToken(refreshToken, isPunchout)
    cookie.setAccessToken(accessToken, isPunchout)
  }

  return async (_: IsAuthenticatedOptions = {}): Promise<boolean> => {
    const provider = cookie.getProvider()
    const selectedContactId = cookie.getContactId()
    const idToken = cookie.getIdToken()
    const refreshToken = cookie.getRefreshToken()
    let accessToken = cookie.getAccessToken()

    const validIdToken = token.getValidToken<IdTokenData>(idToken)
    const validAccessToken = token.getValidToken<AccessTokenData>(accessToken)

    if (!validIdToken) {
      if (!refreshToken) {
        cookie.clearTokens()
        return false
      }

      if (!provider) {
        cookie.clearTokens()
        return false
      }

      const tokens = await authApi.refreshTokens(refreshToken, provider, selectedContactId)

      if (!tokens) {
        // Guard: a concurrent refresh may have already updated the cookies.
        // Re-read the ID token and check validity before clearing.
        const currentIdToken = cookie.getIdToken()
        if (token.isValidToken(currentIdToken)) {
          return true
        }
        cookie.clearTokens()
        return false
      }

      setCookies(provider, tokens.idToken, tokens.refreshToken, tokens.micrositeToken)

      return true
    }

    if (!validAccessToken) {
      accessToken = await authApi.getAccessToken(idToken, selectedContactId)

      if (!accessToken) {
        cookie.clearTokens()
        return false
      }

      const isPunchout = provider === 'dustin_punchout'
      cookie.setAccessToken(accessToken, isPunchout)
    }

    return true
  }
}
